import React, { useState, useEffect } from 'react';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet, apiPost } from '../utils/apiUtils';
import { removeToken } from '../utils/authUtils';
import { trackEvent } from '../utils/analytics';
import { HAS_PAYMENTS, HAS_SUBSCRIPTIONS, HAS_TELEGRAM, IS_EUROPE, localePath } from '../config/region';
import { t } from '../i18n';
import AuthModal from './AuthModal';
import AuthorOfferModal from './AuthorOfferModal';
import ParticleBackground from './ParticleBackground';
import {
  ChevronLeft, User, Star, Link2, Unlink,
  Copy, Check, LogOut, BookOpen, ChevronDown, ChevronUp, Mail, Flag, Newspaper, Crown,
} from 'lucide-react';
import './UserProfile.css';

/* ── Sub-components ─────────────────────────────────────── */

const TelegramVerificationCode = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || '@ainovel_helper_bot';
  const botLink = process.env.REACT_APP_TELEGRAM_BOT_LINK || 'https://t.me/ainovel_helper_bot';
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="up-tg-code">
      <p className="up-tg-code-label">{t('Код верификации')}</p>
      <p className="up-tg-code-inst">
        {t('Скопируйте этот код и отправьте его в Telegram бот для подтверждения:')}
      </p>
      <div className="up-code-row">
        <span className="up-code-value">{code}</span>
        <button className={`up-copy-btn${copied ? ' up-copy-btn--ok' : ''}`} onClick={handleCopy}>
          {copied ? <><Check size={12} /> {t('Скопировано')}</> : <><Copy size={12} /> {t('Копировать')}</>}
        </button>
      </div>
      <div className="up-code-steps">
        <p>1. {t('Скопируйте код выше')}</p>
        <p>2. {t('Откройте')} <a href={botLink} target="_blank" rel="noopener noreferrer">{botName}</a></p>
        <p>3. <strong>{t('Отправьте код в личном сообщении')}</strong></p>
        <p>4. {t('Получите подтверждение и 100 звёзд!')}</p>
      </div>
    </div>
  );
};

const TelegramUsernameInput = ({ onSubmit, loading }) => {
  const [username, setUsername] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username.trim()) onSubmit(username.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="up-tg-form">
      <input
        className="up-tg-input"
        type="text"
        placeholder="@username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        disabled={loading}
      />
      <button
        type="submit"
        className="up-tg-submit"
        disabled={loading || !username.trim()}
      >
        {loading ? t('Привязка…') : t('🔗 Привязать Telegram')}
      </button>
    </form>
  );
};

const MONTH_NAMES = [
  '', t('Январь'), t('Февраль'), t('Март'), t('Апрель'), t('Май'), t('Июнь'),
  t('Июль'), t('Август'), t('Сентябрь'), t('Октябрь'), t('Ноябрь'), t('Декабрь'),
];

const NovelTurnsCard = ({ novel }) => {
  const [expanded, setExpanded] = useState(false);
  const coverUrl =
    novel.cover_background_image_id && novel.cover_background_image_extension
      ? `${GCS_BUCKET_URL}/production/${novel.novel_id}/background/${novel.cover_background_image_id}.${novel.cover_background_image_extension}`
      : null;

  const totalTurns = novel.monthly_turns.reduce((s, m) => s + m.number_of_turns, 0);

  return (
    <div className="up-novel-card" style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <div className="up-novel-cover">
          {coverUrl ? (
            <img className="up-novel-img" src={coverUrl} alt={novel.novel_name} />
          ) : (
            <span className="up-novel-placeholder">📖</span>
          )}
        </div>
        <div className="up-novel-info">
          <div className="up-novel-name">{novel.novel_name}</div>
          <div className="up-novel-desc">{t('Всего ходов: {0}', totalTurns)}</div>
        </div>
        <span style={{ color: 'var(--muted)', flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </div>

      {expanded && (
        <div className="up-turns-table">
          {novel.monthly_turns.length === 0 ? (
            <p className="up-no-novels">{t('Нет данных')}</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr>
                  <th className="up-turns-th">{t('Месяц')}</th>
                  <th className="up-turns-th" style={{ textAlign: 'right' }}>{t('Ходы')}</th>
                </tr>
              </thead>
              <tbody>
                {novel.monthly_turns.map((m) => (
                  <tr key={`${m.year}-${m.month}`}>
                    <td className="up-turns-td">{MONTH_NAMES[m.month]} {m.year}</td>
                    <td className="up-turns-td" style={{ textAlign: 'right' }}>{m.number_of_turns}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Email send cooldown ────────────────────────────────── */

/* How long the player must wait between emails sent from this page. The time of the last send is
   kept in localStorage so reloading the page does not hand the button back. This is only a
   courtesy to the player and to the mail quota: the server does not enforce it, so a direct API
   call still goes through. */
const EMAIL_COOLDOWN_SECONDS = 60;
const emailSentKey = (username) => `up-email-sent-at:${username}`;

const readEmailCooldown = (username) => {
  try {
    const sentAt = parseInt(localStorage.getItem(emailSentKey(username)) || '0', 10);
    return Math.max(0, Math.ceil((sentAt + EMAIL_COOLDOWN_SECONDS * 1000 - Date.now()) / 1000));
  } catch {
    return 0;
  }
};

const markEmailSent = (username) => {
  try { localStorage.setItem(emailSentKey(username), String(Date.now())); } catch { /* storage blocked */ }
};

/* ── Main component ─────────────────────────────────────── */

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [authError, setAuthError] = useState(null);
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [novelsTurns, setNovelsTurns] = useState([]);
  const [novelsTurnsLoading, setNovelsTurnsLoading] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [agencySaving, setAgencySaving] = useState(false);

  // Subscription: daily star claim
  const [subscription, setSubscription] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimMessage, setClaimMessage] = useState(null);

  // Email state
  const [emailInput, setEmailInput] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds until another email may be sent
  const [emailNotice, setEmailNotice] = useState(null); // inline notice in email section

  // Change password state
  const [pwdEditing, setPwdEditing] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdNotice, setPwdNotice] = useState(null);

  // Admin: post news
  const [newsOpen, setNewsOpen] = useState(false);
  const [newsTitle, setNewsTitle] = useState('');
  const [newsSlug, setNewsSlug] = useState('');
  const [newsHtml, setNewsHtml] = useState('');
  const [newsLoading, setNewsLoading] = useState(false);

  const handlePostNews = async (e) => {
    e.preventDefault();
    setNewsLoading(true);
    try {
      await apiPost(
        API_URLS.ADMIN_NEWS,
        { slug: newsSlug.trim(), title: newsTitle.trim(), html_content: newsHtml },
        {},
        true,
        t('Новость опубликована!')
      );
      setNewsTitle(''); setNewsSlug(''); setNewsHtml(''); setNewsOpen(false);
    } catch {
      // apiPost already shows an error toast
    } finally {
      setNewsLoading(false);
    }
  };


  useEffect(() => {
    trackEvent('profile_view');
    fetchUserData();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const startEmailCooldown = (username) => {
    markEmailSent(username);
    setResendCooldown(EMAIL_COOLDOWN_SECONDS);
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await apiGet(API_URLS.USER_ME, {}, false, '', true);
      setUser(response.data);
      // A send made before this page was reloaded still counts.
      if (response.data.username) setResendCooldown(readEmailCooldown(response.data.username));

      // Creators must accept the author offer (оферта) — prompt if not yet accepted
      if (response.data.is_creator && !response.data.author_offer_accepted) {
        setShowOfferModal(true);
      }
      // Fetch novel turns and subscription state if user is registered
      if (response.data.username) {
        fetchNovelsTurns();
        // Europe has no subscription; the endpoint 404s there, so do not ask.
        if (HAS_SUBSCRIPTIONS) fetchSubscription();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // "Place my novels on plotex.io" under the agency agreement — saved as soon as it is ticked.
  // Once given it cannot be withdrawn, so the checkbox locks.
  const acceptAgencyConsent = async () => {
    setAgencySaving(true);
    try {
      await apiPost(API_URLS.AUTHOR_AGENCY, { accepted: true }, {}, true, 'Согласие сохранено');
      setUser(u => ({ ...u, author_agency_accepted: true }));
    } catch {
      // apiPost already shows an error toast
    } finally {
      setAgencySaving(false);
    }
  };

  const fetchNovelsTurns = async () => {
    try {
      setNovelsTurnsLoading(true);
      const response = await apiGet(API_URLS.MY_NOVELS_TURNS, {}, false, '', true);
      setNovelsTurns(response.data.novels || []);
    } catch (err) {
      console.error('Failed to load novels turns:', err);
    } finally {
      setNovelsTurnsLoading(false);
    }
  };


  const fetchSubscription = async () => {
    try {
      const response = await apiGet(API_URLS.SUBSCRIPTION, {}, false, '', true);
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    }
  };

  const handleClaimDaily = async () => {
    if (claimLoading) return;
    setClaimLoading(true);
    setClaimMessage(null);
    try {
      const res = await apiPost(API_URLS.SUBSCRIPTION_CLAIM_DAILY, {}, {}, false, '', true);
      setUser(u => ({ ...u, balance: res.data.new_balance }));
      setSubscription(s => ({ ...s, daily_stars_available: false }));
      setClaimMessage({ ok: true, text: `⭐ +${res.data.stars_added} звёзд! Возвращайтесь завтра.` });
    } catch (err) {
      setClaimMessage({ ok: false, text: err.message || 'Не удалось получить звёзды' });
    } finally {
      setClaimLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setAuthError(null);
    fetchUserData();
  };

  const handleLogout = () => {
    removeToken();
    fetchUserData();
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('ru-RU');

  const handleTelegramUsernameSubmit = async (username) => {
    setLinkingTelegram(true);
    try {
      await apiPost(API_URLS.LINK_TELEGRAM, { telegram_username: username });
      setAuthError(t('Telegram аккаунт успешно привязан!'));
      fetchUserData();
    } catch (error) {
      setAuthError(error.message || t('Ошибка привязки Telegram аккаунта'));
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (!window.confirm(t('Вы уверены, что хотите отвязать Telegram аккаунт? Вы не сможете сбрасывать пароль через Telegram.'))) return;
    setLinkingTelegram(true);
    try {
      await apiPost(API_URLS.UNLINK_TELEGRAM, {});
      setAuthError(t('Telegram аккаунт успешно отвязан!'));
      fetchUserData();
    } catch (error) {
      setAuthError(error.message || t('Ошибка отвязки Telegram аккаунта'));
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleSetEmail = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    // No-op if same email
    if (email === (user.email || '').toLowerCase()) {
      setEmailEditing(false);
      setEmailInput('');
      setEmailPassword('');
      return;
    }
    setEmailLoading(true);
    setEmailNotice(null);
    try {
      await apiPost(API_URLS.SET_EMAIL, { email, current_password: emailPassword }, {}, true, '');
      setEmailEditing(false);
      setEmailInput('');
      setEmailPassword('');
      setEmailNotice({ ok: true, text: t('📨 Email сохранён. Письмо с подтверждением отправлено — проверьте почту.') });
      // Saving a new address sends the confirmation mail too, so "resend" must not be open at once.
      startEmailCooldown(user.username);
      fetchUserData();
    } catch (err) {
      setEmailNotice({ ok: false, text: err.message || t('Ошибка при сохранении email') });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (resendLoading || resendCooldown > 0) return;
    setResendLoading(true);
    setEmailNotice(null);
    try {
      await apiPost(API_URLS.RESEND_CONFIRMATION_EMAIL, {}, {}, true, '');
      startEmailCooldown(user.username);
      setEmailNotice({ ok: true, text: t('📨 Письмо отправлено повторно — проверьте почту (и папку «Спам»).') });
    } catch (err) {
      setEmailNotice({ ok: false, text: err.message || t('Ошибка при отправке письма') });
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdNotice(null);
    if (pwdNew !== pwdConfirm) {
      setPwdNotice({ ok: false, text: t('Новые пароли не совпадают') });
      return;
    }
    if (pwdNew.length < 6) {
      setPwdNotice({ ok: false, text: t('Пароль должен содержать минимум 6 символов') });
      return;
    }
    setPwdLoading(true);
    try {
      await apiPost(API_URLS.CHANGE_PASSWORD, { current_password: pwdCurrent, new_password: pwdNew }, {}, true, '');
      setPwdNotice({ ok: true, text: t('✅ Пароль успешно изменён') });
      setPwdEditing(false);
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
    } catch (err) {
      setPwdNotice({ ok: false, text: err.message || t('Ошибка при изменении пароля') });
    } finally {
      setPwdLoading(false);
    }
  };

  /* The two Telegram notices are the only successes routed through authError. They are matched by
     value because the Russian substring below no longer appears once they are translated. */
  const isOk = authError === t('Telegram аккаунт успешно привязан!')
    || authError === t('Telegram аккаунт успешно отвязан!')
    || authError?.includes('успешно')
    || authError?.startsWith('✅');
  // Only subscribers see the block at all, so no test-cookie gate is needed here.
  const activePlan = subscription?.plans?.find(p => p.id === subscription?.active_plan_id) || null;

  /* ── Render: loading ─────────────────────────────────── */
  if (loading) {
    return (
      <div className="up-wrap">
        <ParticleBackground />
        <div className="up-loading">
          <div className="up-spinner" />
          <span>{t('Загрузка…')}</span>
        </div>
      </div>
    );
  }

  /* ── Render: error ───────────────────────────────────── */
  if (error) {
    return (
      <div className="up-wrap">
        <ParticleBackground />
        <div className="up-error-page">
          <p>{t('Ошибка загрузки данных пользователя')}</p>
          <a className="up-btn-back" href={localePath('/')}>
            <ChevronLeft size={16} /> {t('На главную')}
          </a>
        </div>
      </div>
    );
  }

  /* ── Render: main ────────────────────────────────────── */
  return (
    <div className="up-wrap">
      <ParticleBackground />

      <div className="up-app">

        {/* Header */}
        <div className="up-header">
          <h1 className="up-title">{t('Мой аккаунт')}</h1>
        </div>

        {/* Status message */}
        {authError && (
          <div className={`up-message${isOk ? ' up-message--ok' : ' up-message--err'}`}>
            {authError}
          </div>
        )}

        {/* Auth modal */}
        {showAuthModal && (
          <AuthModal
            onSuccess={handleAuthSuccess}
            onClose={() => setShowAuthModal(false)}
            initialMode={authModalMode}
          />
        )}

        {/* Author offer acceptance (creators only) */}
        {showOfferModal && (
          <AuthorOfferModal
            onAccepted={() => { setShowOfferModal(false); fetchUserData(); }}
          />
        )}

        {/* ── Account ───────────────────────────────────── */}
        <div className="up-section">
          <h2 className="up-section-title">
            <User size={15} /> {t('Аккаунт')}
          </h2>

          {user.username ? (
            <>
              <div className="up-info-item">
                <span className="up-info-label">{t('Пользователь')}</span>
                <span className="up-info-value up-info-value--highlight">{user.username}</span>
              </div>
              <div className="up-info-item">
                <span className="up-info-label">{t('Дата регистрации')}</span>
                <span className="up-info-value">{formatDate(user.join_date)}</span>
              </div>
              <button className="up-btn-logout" onClick={handleLogout}>
                <LogOut size={13} /> {t('Выйти')}
              </button>
            </>
          ) : (
            <>
              <p className="up-guest-note">{t('Гость · войдите для полного доступа')}</p>
              {user.can_get_registration_stars && (
                <div className="up-tg-reward">
                  {HAS_TELEGRAM
                    ? t('⭐ Получите 100 звёзд за регистрацию и 100 звёзд за привязку Telegram аккаунта бесплатно!')
                    : t('⭐ Получите 100 звёзд за регистрацию бесплатно!')}
                </div>
              )}
              <div className="up-info-item">
                <span className="up-info-label">{t('Дата визита')}</span>
                <span className="up-info-value">{formatDate(user.join_date)}</span>
              </div>
              <div className="up-auth-btns">
                <button
                  className="up-btn-register"
                  onClick={() => { setAuthModalMode('register'); setShowAuthModal(true); }}
                >
                  {t('Регистрация')}
                </button>
                <button
                  className="up-btn-login"
                  onClick={() => { setAuthModalMode('login'); setShowAuthModal(true); }}
                >
                  {t('Войти')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Balance ───────────────────────────────────── */}
        {user.is_owner && (
          <div className="up-section">
            <h2 className="up-section-title">
              <Star size={15} /> {t('Баланс')}
            </h2>
            <div className="up-balance-display">
              <span className="up-balance-num">{user.balance}</span>
              <span className="up-balance-unit">{t('звёзд')}</span>
            </div>
            <p className="up-turn-cost">{t('💬 Каждое сообщение персонажам стоит минимум 10 звёзд')}</p>
            {HAS_PAYMENTS && (
              <a href={localePath('/payment')} className="up-btn-payment">{t('Пополнить счёт')}</a>
            )}
          </div>
        )}

        {/* ── Subscription ──────────────────────────────── */}
        {HAS_SUBSCRIPTIONS && user.is_owner && activePlan && (
          <div className="up-section">
            <h2 className="up-section-title">
              <Crown size={15} /> Подписка
            </h2>

            <div className="up-info-item">
              <span className="up-info-label">План</span>
              <span className="up-info-value up-info-value--highlight">{activePlan.name}</span>
            </div>
            <div className="up-info-item">
              <span className="up-info-label">Активна до</span>
              <span className="up-info-value">{formatDate(subscription.end_date)}</span>
            </div>
            <div className="up-info-item">
              <span className="up-info-label">Скидка на пакеты звёзд</span>
              <span className="up-info-value">−{activePlan.discount_percent}%</span>
            </div>
            {subscription.expiring_stars > 0 && (
              <div className="up-sub-warning">
                ⚠️ <b>{subscription.expiring_stars} звёзд</b> подписки сгорят{' '}
                {formatDate(subscription.end_date)} — они выданы на месяц. Ежедневные звёзды
                и купленные пакеты звёзд остаются навсегда.
              </div>
            )}

            {claimMessage && (
              <div className={`up-message${claimMessage.ok ? ' up-message--ok' : ' up-message--err'}`} style={{ marginTop: 12 }}>
                {claimMessage.text}
              </div>
            )}

            {subscription.daily_stars_available && (
              <div className="up-tg-reward" style={{ marginTop: 14 }}>
                ⭐ Заберите {activePlan.daily_stars} звёзд за сегодня — они остаются навсегда!
              </div>
            )}

            <button
              className="up-btn-payment"
              onClick={handleClaimDaily}
              disabled={claimLoading || !subscription.daily_stars_available}
            >
              {claimLoading
                ? 'Загрузка…'
                : subscription.daily_stars_available
                  ? `Забрать ${activePlan.daily_stars} звёзд`
                  : 'Звёзды за сегодня уже получены'}
            </button>
          </div>
        )}

        {/* ── Email ─────────────────────────────────────── */}
        {user.is_owner && user.username && (
          <div className="up-section">
            <h2 className="up-section-title">
              <Mail size={15} /> {t('Email')}
            </h2>

            {/* Inline notice */}
            {emailNotice && (
              <div className={`up-message${emailNotice.ok ? ' up-message--ok' : ' up-message--err'}`} style={{ marginBottom: 12 }}>
                {emailNotice.text}
              </div>
            )}

            {user.email && !emailEditing ? (
              <>
                <div className="up-info-item">
                  <span className="up-info-label">{t('Адрес')}</span>
                  <span className="up-info-value">{user.email}</span>
                </div>
                <div className="up-info-item">
                  <span className="up-info-label">{t('Статус')}</span>
                  <span className="up-info-value">
                    {user.email_confirmed
                      ? t('✅ Подтверждён')
                      : t('⏳ Не подтверждён')}
                  </span>
                </div>
                {!user.email_confirmed && (
                  <>
                    <p className="up-tg-hint" style={{ marginTop: 4 }}>
                      {t('📬 Проверьте входящие и папку «Спам» — мы отправили письмо со ссылкой подтверждения. После подтверждения станет доступен сброс пароля по email.')}
                    </p>
                    <button
                      className="up-btn-secondary"
                      onClick={handleResendConfirmation}
                      disabled={resendLoading || resendCooldown > 0}
                    >
                      {resendLoading
                        ? t('Отправка…')
                        : resendCooldown > 0
                          ? t('📨 Отправить повторно через {0} с', resendCooldown)
                          : t('📨 Отправить письмо повторно')}
                    </button>
                  </>
                )}
                <button
                  className="up-btn-secondary"
                  onClick={() => { setEmailInput(user.email); setEmailEditing(true); setEmailNotice(null); }}
                >                  {t('✏️ Изменить email')}
                </button>
              </>
            ) : (
              <form onSubmit={handleSetEmail} className="up-tg-form">
                {!user.email && (
                  <p className="up-tg-hint">
                    {t('Укажите email для подтверждения аккаунта и восстановления пароля.')}
                  </p>
                )}
                <input
                  className="up-tg-input"
                  type="email"
                  placeholder={t('example@mail.ru')}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  disabled={emailLoading}
                  required
                />
                <input
                  className="up-tg-input"
                  type="password"
                  placeholder={t('Текущий пароль для подтверждения')}
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  disabled={emailLoading}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="submit"
                  className="up-tg-submit"
                  disabled={emailLoading || !emailInput.trim() || !emailPassword.trim()}
                >
                  {emailLoading ? t('Сохранение…') : t('💾 Сохранить email')}
                </button>
                {emailEditing && (
                  <button
                    type="button"
                    className="up-btn-secondary"
                    onClick={() => { setEmailEditing(false); setEmailInput(''); setEmailPassword(''); }}
                    disabled={emailLoading}
                  >
                    {t('Отмена')}
                  </button>
                )}
              </form>
            )}
          </div>
        )}

        {/* ── Change password ───────────────────────────── */}
        {user.is_owner && user.username && (
          <div className="up-section">
            <h2 className="up-section-title">
              🔑 {t('Пароль')}
            </h2>

            {pwdNotice && (
              <div className={`up-message${pwdNotice.ok ? ' up-message--ok' : ' up-message--err'}`} style={{ marginBottom: 12 }}>
                {pwdNotice.text}
              </div>
            )}

            {!pwdEditing ? (
              <button
                className="up-btn-secondary"
                onClick={() => { setPwdEditing(true); setPwdNotice(null); }}
              >
                {t('✏️ Изменить пароль')}
              </button>
            ) : (
              <form onSubmit={handleChangePassword} className="up-tg-form">
                <input
                  className="up-tg-input"
                  type="password"
                  placeholder={t('Текущий пароль')}
                  value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                  disabled={pwdLoading}
                  required
                  autoComplete="current-password"
                />
                <input
                  className="up-tg-input"
                  type="password"
                  placeholder={t('Новый пароль')}
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                  disabled={pwdLoading}
                  required
                  autoComplete="new-password"
                />
                <input
                  className="up-tg-input"
                  type="password"
                  placeholder={t('Подтвердить новый пароль')}
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                  disabled={pwdLoading}
                  required
                  autoComplete="new-password"
                />
                <button type="submit" className="up-tg-submit" disabled={pwdLoading}>
                  {pwdLoading ? t('Сохранение…') : t('💾 Сохранить пароль')}
                </button>
                <button
                  type="button"
                  className="up-btn-secondary"
                  onClick={() => { setPwdEditing(false); setPwdCurrent(''); setPwdNew(''); setPwdConfirm(''); setPwdNotice(null); }}
                  disabled={pwdLoading}
                >
                  {t('Отмена')}
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── Telegram ──────────────────────────────────── */}        {HAS_TELEGRAM && user.is_owner && user.username && (
          <div className="up-section">
            <h2 className="up-section-title">
              <Link2 size={15} /> Telegram
            </h2>

            {user.telegram_star_reward_available && (
              <div className="up-tg-reward">
                {t('⭐ Подтвердите подписку на Telegram и получите 100 звёзд!')}
              </div>
            )}

            {user.telegram_linked ? (
              <div className="up-tg-linked">
                <p className="up-tg-status up-tg-status--ok">{t('✅ Telegram привязан')}</p>
                {user.telegram_username && (
                  <p className="up-tg-username">@{user.telegram_username}</p>
                )}
                <p className="up-tg-recovery">
                  {t('Сбросить пароль: команда /change_password в боте')}
                </p>
                {user.telegram_verification_code && user.telegram_star_reward_available && (
                  <TelegramVerificationCode code={user.telegram_verification_code} />
                )}
                {!linkingTelegram ? (
                  <button className="up-btn-unlink" onClick={handleUnlinkTelegram}>
                    <Unlink size={13} /> {t('Отвязать')}
                  </button>
                ) : (
                  <p className="up-tg-status">{t('Загрузка…')}</p>
                )}
              </div>
            ) : (
              <div className="up-tg-not-linked">
                <p className="up-tg-hint">{t('Привяжите аккаунт для восстановления пароля')}</p>
                <p className="up-tg-warning">
                  {t('⚠️ Убедитесь, что у вашего Telegram аккаунта есть публичный username в настройках')}
                </p>
                <TelegramUsernameInput
                  onSubmit={handleTelegramUsernameSubmit}
                  loading={linkingTelegram}
                />
              </div>
            )}
          </div>
        )}

        {/* ── My Novels Turns ─────────────────────────────── */}
        {user.is_owner && user.username && (
          <div className="up-section">
            <h2 className="up-section-title">
              <BookOpen size={15} /> {t('Мои новеллы')}
            </h2>

            {/* The agency agreement covers Russian authors only, so the checkbox is ainovel.ru's */}
            {!IS_EUROPE && user.is_creator && (
              <label className="up-agency-check">
                <input
                  type="checkbox"
                  checked={!!user.author_agency_accepted}
                  disabled={agencySaving || !!user.author_agency_accepted}
                  onChange={(e) => { if (e.target.checked) acceptAgencyConsent(); }}
                />
                <span>
                  Размещать мои новеллы на платформе plotex.io на условиях{' '}
                  <a href={localePath('/author-agency-agreement')} target="_blank" rel="noopener noreferrer">
                    оферты о заключении агентского договора
                  </a>.
                </span>
              </label>
            )}

            {novelsTurnsLoading ? (
              <p className="up-no-novels">{t('Загрузка…')}</p>
            ) : novelsTurns.length === 0 ? (
              <p className="up-no-novels">{t('У вас нет опубликованных новелл')}</p>
            ) : (
              <div className="up-novels">
                {novelsTurns.map((novel) => (
                  <NovelTurnsCard key={novel.novel_id} novel={novel} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Admin: Reports + Post news ────────────────────── */}
        {user.is_admin && (
          <div className="up-section">
            <h2 className="up-section-title">
              <Flag size={15} /> {t('Администрирование')}
            </h2>
            <div className="up-admin-actions">
              <a href={localePath('/admin/reports')} className="up-btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Flag size={13} /> {t('Жалобы пользователей')}
              </a>
              <button type="button" className="up-btn-secondary" onClick={() => setNewsOpen(o => !o)}>
                <Newspaper size={13} /> {newsOpen ? t('Скрыть форму') : t('Опубликовать новость')}
              </button>
            </div>

            {newsOpen && (
              <form className="up-news-form" onSubmit={handlePostNews}>
                <input
                  className="up-tg-input"
                  type="text"
                  placeholder={t('Заголовок')}
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                />
                <input
                  className="up-tg-input"
                  type="text"
                  placeholder={t('Slug (латиницей, напр. konkurs-iyun)')}
                  value={newsSlug}
                  onChange={(e) => setNewsSlug(e.target.value)}
                />
                <textarea
                  className="up-tg-input up-news-textarea"
                  placeholder={t('HTML-содержимое новости')}
                  value={newsHtml}
                  onChange={(e) => setNewsHtml(e.target.value)}
                  rows={10}
                />
                <button
                  type="submit"
                  className="up-btn-secondary"
                  disabled={newsLoading || !newsTitle.trim() || !newsSlug.trim() || !newsHtml.trim()}
                >
                  {newsLoading ? t('Публикация...') : t('Опубликовать')}
                </button>
              </form>
            )}
          </div>
        )}

        <div className="up-spacer" />
      </div>
    </div>
  );
};

export default UserProfile;
